import React, { useState, useEffect, useRef } from 'react';
import { useAtom } from 'jotai';
import { Button, Tooltip, Typography, message } from 'antd';
import { cloneDeep } from 'lodash';
import { BgColorsOutlined, CompressOutlined, ExpandOutlined } from '@ant-design/icons';
import MappingGrid from './MappingGrid';
import SpectraLineChart from './SpectraLineChart';
import IntensityScatterChart from './IntensityScatterChart';
import { getColors } from '../utils/substancesAndColors';
import './PrimaryCharts.css';
import {
    colorsState
} from '../stateManagement/graphState';
import { showChartsFlag } from '../stateManagement/actionButtonState';
import { scanningState, calibrationInfoState, scanTypeState, calibrateState, scanPhaseState } from '../stateManagement/processState';
import { mapState, userNameState, userGroupsState } from '../stateManagement/commonState';
import Thermal from './Thermal';

const { Text } = Typography;

const defaultLineSpectrum = {
    serial: null,
    index: null,
    rawSpectrum: new Array(500).fill(null).map(_ => 0),
    darkCorrectedSpectrum: new Array(500).fill(null).map(_ => 0),
    badPixelCorrectedSpectrum: new Array(500).fill(null).map(_ => 0),
    baselineCorrectedSpectrum: new Array(500).fill(null).map(_ => 0),
    intensityCorrectedSpectrum: new Array(500).fill(null).map(_ => 0),
    smoothedSpectrum: new Array(500).fill(null).map(_ => 0),
    secondaryBaselineCorrectedSpectrum: new Array(500).fill(null).map(_ => 0),
    normalizedSpectrum: new Array(500).fill(null).map(_ => 0),
    interpolatedSpectrum: new Array(2100).fill(null).map(_ => 0),
    processedSpectrum: new Array(2100).fill(null).map(_ => 0),
    integrationTime: null
}


const PrimaryCharts = ({ currentSerial, currentMapID }) => {
    const [username] = useAtom(userNameState);
    const [userGroups] = useAtom(userGroupsState);
    const [linechartSpectrum, setLinechartSpectrum] = useState(defaultLineSpectrum);
    const [, setColors] = useAtom(colorsState);
    const [wavenumberFilterIndex, setWavenumberFilterIndex] = useState(null);
    const [map] = useAtom(mapState);
    const [showCharts] = useAtom(showChartsFlag);
    const mapSerial = useRef(null);
    const [scanning] = useAtom(scanningState);
    // const [calibrationInfo, setCalibrationInfo] = useAtom(calibrationInfoState);
    const [scanType] = useAtom(scanTypeState);
    const [calibrate, setCalibrate] = useAtom(calibrateState);
    const [scanPhase] = useAtom(scanPhaseState);

    useEffect(() => {
        mapSerial.current = currentSerial;
        if (!currentSerial) {
            setLinechartSpectrum(defaultLineSpectrum);
        }
    }, [currentSerial]);

    const updateSpectrumLineChart = e => {
        e.serial = mapSerial.current;
        setLinechartSpectrum(e);
    }

    const changeColors = _ => {
        if (map.length > 0) {
            setColors(getColors());
        } else {
            message.info({ content: `No data to change colors`, duration: 1.5 });
        }
    }

    const updateWavenumberFilter = wavenumberIndex => {
        setWavenumberFilterIndex(wavenumberIndex);
    }

    const toggleCalibration = () => {
        if (!scanning) {
            setCalibrate(prev => {
                const calibrate = !prev;
                if (calibrate) {
                    message.success({ content: `Pre-scan calibration enabled`, duration: 3 });
                } else {
                    message.info({ content: `Pre-scan calibration disabled`, duration: 3 });
                }
                return calibrate;
            });
        } else {
            message.warning({ content: `Cannot toggle calibration settings while scanning`, duration: 3 });
        }
    }

    return (
        <div style={{ position: `relative` }}>
            <Tooltip title={`${calibrate ? 'Disable calibration' : 'Enable calibration'}`}>
                <Button
                    icon={calibrate ? <CompressOutlined /> : <ExpandOutlined />}
                    onClick={toggleCalibration}
                    style={{ float: 'left', marginLeft: '15%', zIndex: 2 }}
                    disabled={scanning || scanType === 'reference' || scanPhase === 'Finished'}
                ></Button>
            </Tooltip>
            {currentSerial !== null &&
                <Text style={{ position: `absolute`, left: `50%`, transform: `translateX(-50%)`, top: `15px` }}>
                    Serial: {currentSerial}
                </Text>
            }
            <Button
                icon={<BgColorsOutlined />}
                onClick={changeColors}
                style={{ float: 'right', marginRight: '15%', zIndex: 2 }}
            />
            <MappingGrid updateSpectrumLineChart={updateSpectrumLineChart} wavenumberFilterIndex={wavenumberFilterIndex} />
            {showCharts &&
                <>
                    <SpectraLineChart
                        linechartSpectrum={linechartSpectrum}
                        updateWavenumberFilter={updateWavenumberFilter}
                    />
                    {(username && userGroups.length && userGroups.includes('pro_user')) ?
                        <IntensityScatterChart
                            updateSpectrumLineChart={updateSpectrumLineChart}
                        />
                        : null}
                </>}
            <div style={{
                display: (showCharts && (username && userGroups.length && userGroups.includes('pro_user'))) ? 'block' : 'none',
                opacity: (showCharts && (username && userGroups.length && userGroups.includes('pro_user'))) ? 1 : 0,
                visibility: (showCharts && (username && userGroups.length && userGroups.includes('pro_user'))) ? 'visible' : 'hidden',
                transition: 'opacity 1s ease, visibility 1s ease',
            }}>
                <Thermal currentMapID={currentMapID} />
            </div>
        </div>
    )
}

export default PrimaryCharts
